import { addMonths, format, parseISO } from 'date-fns'

/** One released result, as the card needs it: when the visit was, and what it scored. */
export type ScoreReading = {
	appointmentId: number
	/**
	 * The date of the visit the score belongs to, not the day the result was released.
	 *
	 * Optional only for as long as the history endpoint is missing: `/appointment-results/latest`
	 * returns a score and an appointment id and no date whatsoever, so the one reading the app can
	 * read today has nowhere to get a month from. A reading without one draws its bar and leaves
	 * the space under it empty, which is the truth — better than labelling it with today, which is
	 * the date of the *reading* rather than of the visit. Make it required once the series lands.
	 */
	on?: string
	score: number
}

/**
 * A slot in the chart: either a reading, or the space one will take.
 *
 * A ghost carries a height rather than a score, because it has no score — what it is drawn at is
 * a decision about what the empty chart should say, not a value anybody measured.
 */
export type ScoreSlot =
	| { kind: 'reading'; score: number; label: string; newest: boolean }
	| { kind: 'ghost'; at: number; label: string }

/**
 * Four, which is two years at two cleanings a year.
 *
 * Long enough that filling it means something, short enough that a member can imagine doing it.
 * Fewer and the chart fills before it reads as a series; more and it is a row of dashes that
 * never completes. Past four the window slides: the last four readings, and the rest live in the
 * visit list under the card.
 */
export const SLOTS = 4

/**
 * The bottom of the chart is sixty, not nought.
 *
 * Scores live in a band — seventy-nine and ninety-one are a real climb and, measured from nought,
 * twelve pixels apart. Truncating the axis is what makes the difference visible.
 *
 * It is only honest because every bar carries its own number: the reader is given the values, so
 * the heights are a comparison aid rather than the claim itself. **If the numerals ever come off
 * the bars, this has to go back to nought** — the two are one decision, not two.
 */
export const FLOOR = 60

/** The climb the empty chart is drawn at: the scale, ending at the top of it. */
const EMPTY_CLIMB = [70, 80, 90, 100]

/**
 * How far apart the projected visits in the empty chart are.
 *
 * Only ever used where there is no history to reason from. It is an expectation rather than a
 * booking, and the honest version of this reads the member's own plan; three months is the
 * cadence to assume until it does.
 */
const PROJECTED_MONTHS = 3

/** "MAR 25" — the month and the year, because the window spans years and two MARs collide. */
const monthLabel = (date: Date) => format(date, 'MMM yy')

/** What a slot says where its date will go, once there is real history to read it against. */
const WAITING = '—'

/** Oldest first. A reading with no date sorts first: the only one is the interim single latest. */
const at = (reading: ScoreReading) => (reading.on ? Date.parse(reading.on) : 0)
const byDate = (a: ScoreReading, b: ScoreReading) => at(a) - at(b)

/**
 * The chart's four slots, oldest first.
 *
 * Two shapes, and the difference between them is the whole design:
 *
 * With nothing to show, the ghosts **climb** to the top of the scale and carry the visits a
 * member can expect. Nobody has a score, so the rising row is a statement about what the card is
 * for rather than about the person reading it.
 *
 * With readings in it, the ghosts keep climbing from the newest one — evenly, to the top of the
 * scale — and their dates become dashes, because a date under an empty slot would be claiming to
 * know when the next visit is.
 *
 * The climb was held level at one point, on the argument that a rising ghost beside a real bar
 * predicts a member's next three readings. Drawn, it read as a chart that had given up: four
 * identical heights, no shape, nothing to fill. The ramp is what makes an empty slot legible as
 * a slot — so it stays, and the thing to watch is a low score, where three ghosts rising to a
 * hundred are the most optimistic thing on the screen.
 */
export const buildScoreSeries = (readings: ScoreReading[], now: Date): ScoreSlot[] => {
	const history = [...readings].sort(byDate).slice(-SLOTS)

	if (history.length === 0) {
		return EMPTY_CLIMB.map((at, index) => ({
			kind: 'ghost' as const,
			at,
			label: monthLabel(addMonths(now, PROJECTED_MONTHS * (index + 1))),
		}))
	}

	// `at(-1)` on a list already known to be non-empty, told to the compiler rather than asserted:
	// a `!` here would be the only unchecked claim in the file.
	const [newest] = history.slice(-1)
	if (!newest) return []

	const filled: ScoreSlot[] = history.map((reading) => ({
		kind: 'reading' as const,
		score: reading.score,
		label: reading.on ? monthLabel(parseISO(reading.on)) : '',
		newest: reading === newest,
	}))

	// Evenly from the newest reading up to the top of the scale, so four slots always end at a
	// hundred whether there is one reading or three.
	const gaps = SLOTS - filled.length
	const waiting: ScoreSlot[] = Array.from({ length: gaps }, (_, index) => ({
		kind: 'ghost' as const,
		at: newest.score + ((100 - newest.score) * (index + 1)) / gaps,
		label: WAITING,
	}))

	return [...filled, ...waiting]
}

/** Where a slot's bar reaches, as a fraction of the chart's height. */
export const heightOf = (slot: ScoreSlot) => {
	const value = slot.kind === 'reading' ? slot.score : slot.at
	return Math.max(0, Math.min(1, (value - FLOOR) / (100 - FLOOR)))
}
